// Digital banking set-up API — GET/POST /onboarding/applications/{id}/digital-banking.
// Net banking, UPI, debit card, cheque book, alerts and consents in one call.
import { get, post } from './client';

export interface DigitalBankingConsent {
  type: string;
  accepted: boolean;
  documentVersion?: string;
}

// UI-facing shape. The adapter below translates the legacy screen values to the
// exact Step 9 contract (estatement, SMS_OTP, CURRENT_ADDRESS, etc.).
export interface DigitalBankingData {
  digitalEnabled: boolean;
  secondFactor: string;
  upiEnabled: boolean;
  upiId?: string | null;
  cardVariant?: string | null;
  cardType?: string | null;
  nameOnCard?: string | null;
  cardDelivery?: string | null;
  collectionBranch?: string | null;
  domesticAtm: boolean;
  domesticPos: boolean;
  domesticOnline: boolean;
  contactless: boolean;
  international: boolean;
  atmLimitRupees?: number;
  posLimitRupees?: number;
  wantsChequeBook: boolean;
  chequeLeaves?: number;
  chequeName?: string | null;
  whatsappAlerts: boolean;
  eStatement: boolean;
  statementFrequency?: string;
  physicalStatement: boolean;
  consents?: DigitalBankingConsent[];
}

interface DigitalBankingResponse extends Omit<DigitalBankingData, 'eStatement'> {
  estatement: boolean;
}

const base = (applicationId: string) =>
  `/onboarding/applications/${encodeURIComponent(applicationId)}/digital-banking`;

const fromApi = (body: DigitalBankingResponse): DigitalBankingData => {
  const { estatement, ...rest } = body;
  return {
    ...rest,
    eStatement: estatement,
    secondFactor: body.secondFactor === 'SMS_OTP' ? 'OTP' : body.secondFactor,
    cardVariant: body.cardVariant === 'RUPAY_CLASSIC' ? 'CLASSIC' : body.cardVariant,
    cardType:
      body.cardType === 'VIRTUAL_ONLY'
        ? 'VIRTUAL'
        : body.cardType === 'PHYSICAL_ONLY'
          ? 'PHYSICAL'
          : body.cardType,
    cardDelivery: body.cardDelivery === 'CURRENT_ADDRESS' ? 'HOME' : body.cardDelivery,
  };
};

const toApi = (body: DigitalBankingData): DigitalBankingResponse => {
  const { eStatement, ...rest } = body;
  return {
    ...rest,
    secondFactor: body.secondFactor === 'OTP' ? 'SMS_OTP' : body.secondFactor,
    cardVariant: body.cardVariant === 'CLASSIC' ? 'RUPAY_CLASSIC' : body.cardVariant,
    cardType:
      body.cardType === 'VIRTUAL'
        ? 'VIRTUAL_ONLY'
        : body.cardType === 'PHYSICAL'
          ? 'PHYSICAL_ONLY'
          : body.cardType,
    cardDelivery: body.cardDelivery === 'HOME' ? 'CURRENT_ADDRESS' : body.cardDelivery,
    estatement: eStatement,
  };
};

export const getDigitalBanking = async (applicationId: string): Promise<DigitalBankingData> =>
  fromApi(await get<DigitalBankingResponse>(base(applicationId)));

export const saveDigitalBanking = async (
  applicationId: string,
  body: DigitalBankingData
): Promise<DigitalBankingData> =>
  fromApi(await post<DigitalBankingResponse>(base(applicationId), toApi(body)));
